import { useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { Upload, FileJson, AlertCircle } from 'lucide-react';
import { useHARStore } from '@/stores/har-store';
import { readFileAsText, cn } from '@/lib/utils';

export function FileDropzone() {
  const { loadHAR, isLoading, error } = useHARStore();

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (!file) return;

    try {
      const content = await readFileAsText(file);
      loadHAR(content, file.name);
    } catch (e) {
      console.error('Failed to read file:', e);
    }
  }, [loadHAR]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/json': ['.har', '.json'],
    },
    multiple: false,
  });

  return (
    <div className="w-full max-w-2xl">
      <div
        {...getRootProps()}
        className={cn(
          'dropzone flex flex-col items-center justify-center text-center',
          isDragActive && 'dropzone-active',
          isLoading && 'opacity-50 pointer-events-none'
        )}
      >
        <input {...getInputProps()} />
        
        <div className="mb-6">
          {isDragActive ? (
            <div className="w-16 h-16 rounded-full bg-brand-100 dark:bg-brand-500/20 flex items-center justify-center">
              <FileJson className="w-8 h-8 text-brand-600 dark:text-brand-400" />
            </div>
          ) : (
            <div className="w-16 h-16 rounded-full bg-slate-200 dark:bg-slate-800 flex items-center justify-center">
              <Upload className="w-8 h-8 text-slate-500 dark:text-slate-400" />
            </div>
          )}
        </div>

        <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-100 mb-2">
          {isDragActive ? 'Drop your HAR file here' : 'Drop HAR file to analyze'}
        </h2>

        <p className="text-slate-500 dark:text-slate-400 text-sm mb-4">
          or click to select a file
        </p>

        <div className="flex items-center gap-2 text-xs text-slate-400 dark:text-slate-500">
          <FileJson className="w-4 h-4" />
          <span>Supports .har and .json files</span>
          <span className="text-slate-300 dark:text-slate-600">|</span>
          <kbd className="px-1.5 py-0.5 bg-slate-100 dark:bg-slate-800 rounded text-[10px] font-mono">
            ⌘O
          </kbd>
          <span>to open</span>
        </div>
      </div>

      {error && (
        <div className="mt-4 p-4 bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/30 rounded-lg flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-500 dark:text-red-400 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="text-sm font-medium text-red-600 dark:text-red-400">Failed to parse HAR file</h3>
            <p className="text-sm text-red-500 dark:text-red-400/80 mt-1">{error}</p>
          </div>
        </div>
      )}

      <div className="mt-8 text-center">
        <p className="text-xs text-slate-400 dark:text-slate-600">
          🔒 All processing happens in your browser. Your data never leaves your device.
        </p>
      </div>
    </div>
  );
}
